import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { Button } from "antd";
import {
  createOffer,
  updateOffer,
  getOffersList,
} from "../../appRedux/actions/offers";
import { getPlatformsList } from "../../appRedux/actions/platforms";
import { getSubscriptionsList } from "../../appRedux/actions/subscriptions";
import { hideDialog as hideDialogCommon } from "../../appRedux/actions/common";
import config from "../../utils/config";
import { ImageStudio } from "../Common/ImageStudio";
import SubscriptionLinker from "./SubscriptionLinker";

const LEVELS = [
  { value: "GLOBAL", label: "Global" },
  { value: "MERCHANT", label: "Merchant" },
  { value: "COURSE", label: "Course" },
];
const STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];
const DISCOUNT_TYPES = [
  { value: "PERCENT", label: "Percentage" },
  { value: "FLAT", label: "Flat amount" },
];

const INITIAL = {
  title: "",
  description: "",
  level: "GLOBAL",
  merchant: "",
  courseId: "",
  discountType: "PERCENT",
  discountValue: "",
  maxDiscountAmount: "",
  couponCode: "",
  ctaText: "Learn More",
  ctaLink: "",
  bannerText: "",
  bannerImageUrl: "",
  patchText: "",
  patchImageUrl: "",
  squareCardText: "",
  squareCardImageUrl: "",
  stickerLabel: "",
  promotion: { showOnCoursePage: false, showOnHeaderStrip: false, showInCarousel: false },
  priority: 100,
  status: "DRAFT",
  startAt: "",
  endAt: "",
  linkedSubscriptions: [],
};

// Helper to compute discount
const computeDiscountedPrice = (price, discountValue, discountType, maxDiscountAmount) => {
  const originalPrice = parseFloat(price);
  if (isNaN(originalPrice)) return null;

  const val = parseFloat(discountValue);
  if (isNaN(val) || val <= 0) return originalPrice;

  let discount = 0;
  if (discountType === 'PERCENT') {
    discount = originalPrice * (val / 100);
    const maxAmt = parseFloat(maxDiscountAmount);
    if (maxDiscountAmount && !isNaN(maxAmt)) {
      discount = Math.min(discount, maxAmt);
    }
  } else {
    discount = val;
  }

  return Math.max(0, originalPrice - discount);
};

function FormView({ isUpdate }) {
  const dispatch = useDispatch();
  const offerInfo = useSelector((state) => state.offers.offerInfo);
  const platformList = useSelector((state) => state.platforms.platformList || []);
  const subscriptionList = useSelector((state) => state.subscriptions.subscriptionList || []);
  const [initialValues, setInitialValues] = useState(INITIAL);
  const [activeImageSlot, setActiveImageSlot] = useState(null); // 'banner', 'patch', or 'square'
  const [showManualUrl, setShowManualUrl] = useState(false);

  useEffect(() => {
    dispatch(getPlatformsList({ page: 1, size: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (isUpdate && offerInfo && offerInfo._id) {
      setInitialValues({
        title: offerInfo.title || "",
        description: offerInfo.description || "",
        level: offerInfo.level || "GLOBAL",
        merchant: offerInfo.merchant || "",
        courseId: offerInfo.courseId?._id || offerInfo.courseId || "",
        discountType: offerInfo.discountType || "PERCENT",
        discountValue: offerInfo.discountValue ?? "",
        maxDiscountAmount: offerInfo.maxDiscountAmount ?? "",
        couponCode: offerInfo.couponCode || "",
        ctaText: offerInfo.ctaText || "Learn More",
        ctaLink: offerInfo.ctaLink || "",
        bannerText: offerInfo.bannerText || "",
        bannerImageUrl: offerInfo.bannerImageUrl || "",
        patchText: offerInfo.patchText || "",
        patchImageUrl: offerInfo.patchImageUrl || "",
        squareCardText: offerInfo.squareCardText || "",
        squareCardImageUrl: offerInfo.squareCardImageUrl || "",
        stickerLabel: offerInfo.stickerLabel || "",
        promotion: {
          showOnCoursePage: offerInfo.promotion?.showOnCoursePage || false,
          showOnHeaderStrip: offerInfo.promotion?.showOnHeaderStrip || false,
          showInCarousel: offerInfo.promotion?.showInCarousel || false,
        },
        priority: offerInfo.priority ?? 100,
        status: offerInfo.status || "DRAFT",
        startAt: offerInfo.startAt
          ? new Date(offerInfo.startAt).toISOString().slice(0, 16)
          : "",
        endAt: offerInfo.endAt
          ? new Date(offerInfo.endAt).toISOString().slice(0, 16)
          : "",
        linkedSubscriptions: (offerInfo.linkedSubscriptions || []).map((item) => ({
          subscriptionId:
            typeof item.subscriptionId === "object" && item.subscriptionId !== null
              ? item.subscriptionId._id || item.subscriptionId
              : item.subscriptionId,
          pricingOptionIds: item.pricingOptionIds || [],
        })),
      });
    } else {
      setInitialValues(INITIAL);
    }
  }, [isUpdate, offerInfo]);

  const schema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    level: Yup.string().oneOf(["GLOBAL", "MERCHANT", "COURSE"]).required(),
    merchant: Yup.string().when("level", {
      is: "MERCHANT",
      then: (s) => s.required("Merchant is required for Merchant level"),
    }),
    courseId: Yup.string().when("level", {
      is: "COURSE",
      then: (s) => s.required("Course is required for Course level"),
    }),
    discountType: Yup.string().oneOf(["PERCENT", "FLAT"]).required(),
    discountValue: Yup.number()
      .min(0, "Must be ≥ 0")
      .when("discountType", {
        is: "PERCENT",
        then: (n) => n.max(100, "Percentage must be ≤ 100"),
      })
      .required("Discount value is required"),
    priority: Yup.number().min(0).optional(),
    couponCode: Yup.string().optional().nullable(),
    ctaText: Yup.string().optional().nullable(),
    ctaLink: Yup.string()
      .transform((value) => (value === "" ? null : value))
      .url("Must be a valid URL")
      .optional()
      .nullable(),
    bannerText: Yup.string().optional().nullable(),
    bannerImageUrl: Yup.string()
      .transform((value) => (value === "" ? null : value))
      .url("Must be a valid URL")
      .optional()
      .nullable(),
    patchText: Yup.string().optional().nullable(),
    patchImageUrl: Yup.string()
      .transform((value) => (value === "" ? null : value))
      .url("Must be a valid URL")
      .optional()
      .nullable(),
    squareCardText: Yup.string().optional().nullable(),
    squareCardImageUrl: Yup.string()
      .transform((value) => (value === "" ? null : value))
      .url("Must be a valid URL")
      .optional()
      .nullable(),
    stickerLabel: Yup.string().optional().nullable(),
    promotion: Yup.object().shape({
      showOnCoursePage: Yup.boolean().optional(),
      showOnHeaderStrip: Yup.boolean().optional(),
      showInCarousel: Yup.boolean().optional(),
    }),
    linkedSubscriptions: Yup.array().of(
      Yup.object().shape({
        subscriptionId: Yup.string().required(),
        pricingOptionIds: Yup.array().of(Yup.string()).optional(),
      })
    ).optional(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: (values) => {
      const payload = {
        title: values.title,
        description: values.description,
        level: values.level,
        merchant: values.level === "MERCHANT" ? values.merchant : undefined,
        courseId: values.level === "COURSE" ? values.courseId : undefined,
        discountType: values.discountType,
        discountValue: Number(values.discountValue),
        maxDiscountAmount:
          values.maxDiscountAmount !== ""
            ? Number(values.maxDiscountAmount)
            : null,
        couponCode: values.couponCode || null,
        ctaText: values.ctaText || "Learn More",
        ctaLink: values.ctaLink || null,
        bannerText: values.bannerText || "",
        bannerImageUrl: values.bannerImageUrl || null,
        patchText: values.patchText || "",
        patchImageUrl: values.patchImageUrl || null,
        squareCardText: values.squareCardText || "",
        squareCardImageUrl: values.squareCardImageUrl || null,
        stickerLabel: values.stickerLabel || "",
        promotion: {
          showOnCoursePage: values.promotion?.showOnCoursePage === true || values.promotion?.showOnCoursePage === "true",
          showOnHeaderStrip: values.promotion?.showOnHeaderStrip === true || values.promotion?.showOnHeaderStrip === "true",
          showInCarousel: values.promotion?.showInCarousel === true || values.promotion?.showInCarousel === "true",
        },
        priority: Number(values.priority) || 100,
        status: values.status,
        startAt: values.startAt || null,
        endAt: values.endAt || null,
        linkedSubscriptions: values.level === "MERCHANT" ? values.linkedSubscriptions : [],
      };

      if (isUpdate && offerInfo?._id) {
        dispatch(updateOffer(offerInfo._id, payload))
          .then(() => {
            dispatch(getOffersList()).catch(() => {});
            dispatch(hideDialogCommon());
          })
          .catch(() => {});
      } else {
        dispatch(createOffer(payload))
          .then(() => {
            dispatch(getOffersList()).catch(() => {});
            dispatch(hideDialogCommon());
          })
          .catch(() => {});
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    getFieldProps,
  } = formik;

  // Fetch subscriptions for selected merchant
  useEffect(() => {
    if (values.level === "MERCHANT" && values.merchant) {
      dispatch(getSubscriptionsList({ merchant: values.merchant, size: 100 }));
    }
  }, [values.level, values.merchant, dispatch]);

  const merchants = platformList || [];

  // Calculate preview items for subscriptions
  const previewItems = [];
  if (values.level === "MERCHANT") {
    (values.linkedSubscriptions || []).forEach((link) => {
      const sub = subscriptionList.find((s) => s._id === link.subscriptionId);
      if (!sub) return;

      const options = sub.pricingOptions || [];
      options.forEach((opt) => {
        const identifier = opt._id || opt.name;
        const isSelected =
          link.pricingOptionIds.length === 0 || link.pricingOptionIds.includes(identifier);

        if (isSelected && opt.isActive !== false) {
          const originalPrice = parseFloat(opt.price);
          const hasPrice = (opt.priceType === "PAID" || !opt.priceType) && !isNaN(originalPrice);
          const finalPrice = hasPrice
            ? computeDiscountedPrice(
                originalPrice,
                values.discountValue,
                values.discountType,
                values.maxDiscountAmount
              )
            : null;

          previewItems.push({
            subName: sub.title,
            optName: opt.name || opt.billingCycle,
            priceType: opt.priceType,
            originalPrice,
            finalPrice,
            currency: opt.currency || "INR",
            billingCycle: opt.billingCycle,
          });
        }
      });
    });
  }

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleSubmit} className="signin-form">
        <div className="row">
          {/* Left Column: Form Fields and Submit */}
          <div className="col-lg-7 col-md-12">
            <div className="row m-0">
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">Title <span className="text-danger">*</span></label>
                  <input
                    className="form-control"
                    type="text"
                    {...getFieldProps("title")}
                  />
                  {touched.title && errors.title && (
                    <p className="error-msg text-danger">{errors.title}</p>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    {...getFieldProps("description")}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Level <span className="text-danger">*</span></label>
                  <select
                    className="form-control"
                    {...getFieldProps("level")}
                    onChange={(e) => {
                      setFieldValue("level", e.target.value);
                      setFieldValue("merchant", "");
                      setFieldValue("courseId", "");
                      setFieldValue("linkedSubscriptions", []);
                    }}
                  >
                    {LEVELS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {values.level === "MERCHANT" && (
                <div className="col-md-6">
                  <div className="input-block mb-3">
                    <label className="col-form-label">Merchant <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      {...getFieldProps("merchant")}
                      onChange={(e) => {
                        setFieldValue("merchant", e.target.value);
                        setFieldValue("linkedSubscriptions", []);
                      }}
                    >
                      <option value="">Select merchant</option>
                      {merchants.map((m) => (
                        <option key={m._id || m.code} value={m.code}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                    {touched.merchant && errors.merchant && (
                      <p className="error-msg text-danger">{errors.merchant}</p>
                    )}
                  </div>
                </div>
              )}
              {values.level === "COURSE" && (
                <div className="col-md-6">
                  <div className="input-block mb-3">
                    <label className="col-form-label">Course ID <span className="text-danger">*</span></label>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="MongoDB ObjectId of the course"
                      {...getFieldProps("courseId")}
                    />
                    {touched.courseId && errors.courseId && (
                      <p className="error-msg text-danger">{errors.courseId}</p>
                    )}
                  </div>
                </div>
              )}
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Discount type <span className="text-danger">*</span></label>
                  <select className="form-control" {...getFieldProps("discountType")}>
                    {DISCOUNT_TYPES.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Discount value {values.discountType === "PERCENT" ? "(%)" : "(₹)"} <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="number"
                    min={0}
                    max={values.discountType === "PERCENT" ? 100 : undefined}
                    step="any"
                    {...getFieldProps("discountValue")}
                  />
                  {touched.discountValue && errors.discountValue && (
                    <p className="error-msg text-danger">{errors.discountValue}</p>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Max discount amount (optional)</label>
                  <input
                    className="form-control"
                    type="number"
                    min={0}
                    step="any"
                    {...getFieldProps("maxDiscountAmount")}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Priority</label>
                  <input
                    className="form-control"
                    type="number"
                    min={0}
                    {...getFieldProps("priority")}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Status</label>
                  <select className="form-control" {...getFieldProps("status")}>
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Coupon Code (optional)</label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="e.g. COUPON50"
                    {...getFieldProps("couponCode")}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">CTA Text</label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Learn More"
                    {...getFieldProps("ctaText")}
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">CTA Link (optional)</label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="https://..."
                    {...getFieldProps("ctaLink")}
                  />
                  {touched.ctaLink && errors.ctaLink && (
                    <p className="error-msg text-danger">{errors.ctaLink}</p>
                  )}
                </div>
              </div>

              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">Sticker Label (optional)</label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="E.g. SAVE 20% or SUMMER DEALS"
                    {...getFieldProps("stickerLabel")}
                  />
                </div>
              </div>

              <div className="col-md-12">
                <div className="mb-4 p-3 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <label className="fw-bold mb-2 d-block" style={{ color: "#1e293b", fontSize: "15px" }}>Promotion Settings (3 Levels)</label>
                  
                  <div className="form-check form-switch mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showOnCoursePage"
                      checked={values.promotion?.showOnCoursePage}
                      onChange={(e) => setFieldValue("promotion.showOnCoursePage", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showOnCoursePage">Level 1: Show on Course Pages (Sticky Sidebar Patch)</label>
                  </div>

                  <div className="form-check form-switch mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showOnHeaderStrip"
                      checked={values.promotion?.showOnHeaderStrip}
                      onChange={(e) => setFieldValue("promotion.showOnHeaderStrip", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showOnHeaderStrip">Level 2: Show in Global Header Strip</label>
                  </div>

                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showInCarousel"
                      checked={values.promotion?.showInCarousel}
                      onChange={(e) => setFieldValue("promotion.showInCarousel", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showInCarousel">Level 3: Show in Homepage Banner Carousel</label>
                  </div>
                </div>
              </div>

            </div> {/* row m-0 */}
          </div> {/* col-lg-7 col-md-12 */}

          {/* Right Column: Subscription Linker & Scheduling */}
          <div className="col-lg-5 col-md-12">
            {values.level === "MERCHANT" ? (
              <SubscriptionLinker
                formik={formik}
                subscriptionList={subscriptionList}
                merchantList={platformList}
              />
            ) : (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', textAlign: 'center', color: '#64748b' }}>
                <i className="fa fa-info-circle mb-2" style={{ fontSize: '24px', color: '#3b82f6' }} />
                <p className="mb-0" style={{ fontSize: '13px', fontWeight: 500 }}>
                  Subscription Linker is only active when Offer Level is set to <strong>Merchant</strong>.
                </p>
              </div>
            )}

            {/* Start at / End at fields (Moved here below the subscription section) */}
            <div className="row mt-3">
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Start at (optional)</label>
                  <input
                    className="form-control"
                    type="datetime-local"
                    {...getFieldProps("startAt")}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">End at (optional)</label>
                  <input
                    className="form-control"
                    type="datetime-local"
                    {...getFieldProps("endAt")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full Width Section: Offer Media Placements */}
        <div className="row mt-4 mx-0">
          <div className="col-12 px-0">
            <div className="p-4 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <label className="fw-bold mb-4 d-block" style={{ color: "#1e293b", fontSize: "18px" }}>Offer Media Placements</label>
              <div className="row">
              
              {/* Slot 1: Banner */}
              <div className="col-md-12 col-xl-4 mb-4 mb-xl-0 border-xl-end pe-xl-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="fw-bold m-0" style={{ fontSize: "15px", color: "#334155" }}>1. Homepage Banner</label>
                  <small className="text-muted">Optimal: 1200x400 (3:1)</small>
                </div>
                <input className="form-control mb-3" type="text" placeholder="Promo text for banner" {...getFieldProps("bannerText")} />
                <div className="d-flex gap-2 align-items-center mt-2">
                  {values.bannerImageUrl && (
                    <div style={{ width: "60px", height: "40px", borderRadius: "4px", overflow: "hidden", border: "1px solid #cbd5e1" }}>
                      <img src={values.bannerImageUrl} alt="Banner" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  )}
                  <Button size="small" type="primary" onClick={() => setActiveImageSlot("banner")}>🎨 Image Studio</Button>
                  <Button size="small" type="link" onClick={() => setShowManualUrl(!showManualUrl)}>URL</Button>
                </div>
                {showManualUrl && <input className="form-control mt-2" type="text" placeholder="Image URL" {...getFieldProps("bannerImageUrl")} />}
              </div>

              {/* Slot 2: Sidebar Patch */}
              <div className="col-md-12 col-xl-4 mb-4 mb-xl-0 border-xl-end px-xl-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="fw-bold m-0" style={{ fontSize: "15px", color: "#334155" }}>2. Course Sidebar Patch</label>
                  <small className="text-muted">Optimal: 400x400 or 300x500</small>
                </div>
                <input className="form-control mb-3" type="text" placeholder="Promo text for sidebar patch" {...getFieldProps("patchText")} />
                <div className="d-flex gap-2 align-items-center mt-2">
                  {values.patchImageUrl && (
                    <div style={{ width: "40px", height: "40px", borderRadius: "4px", overflow: "hidden", border: "1px solid #cbd5e1" }}>
                      <img src={values.patchImageUrl} alt="Patch" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  )}
                  <Button size="small" type="primary" onClick={() => setActiveImageSlot("patch")}>🎨 Image Studio</Button>
                </div>
                {showManualUrl && <input className="form-control mt-2" type="text" placeholder="Image URL" {...getFieldProps("patchImageUrl")} />}
              </div>

              {/* Slot 3: Square Card */}
              <div className="col-md-12 col-xl-4 ps-xl-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="fw-bold m-0" style={{ fontSize: "15px", color: "#334155" }}>3. Square Grid Card</label>
                  <small className="text-muted">Optimal: 500x500 (1:1)</small>
                </div>
                <input className="form-control mb-3" type="text" placeholder="Promo text for square card" {...getFieldProps("squareCardText")} />
                <div className="d-flex gap-2 align-items-center mt-2">
                  {values.squareCardImageUrl && (
                    <div style={{ width: "40px", height: "40px", borderRadius: "4px", overflow: "hidden", border: "1px solid #cbd5e1" }}>
                      <img src={values.squareCardImageUrl} alt="Square" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  )}
                  <Button size="small" type="primary" onClick={() => setActiveImageSlot("square")}>🎨 Image Studio</Button>
                </div>
                {showManualUrl && <input className="form-control mt-2" type="text" placeholder="Image URL" {...getFieldProps("squareCardImageUrl")} />}
              </div>

              </div> {/* End of row */}
            </div>
          </div>
        </div>

        {/* Row 2: Full-Width Live Preview Section */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="live-preview-sidebar-card" style={{ position: 'static', width: '100%', marginBottom: '20px' }}>
              <div className="preview-header" style={{ justifyContent: 'space-between' }}>
                <div>
                  <i className="fa fa-eye mr-2" /> Live Offer Preview
                </div>
                {values.bannerImageUrl && (
                  <Button 
                    type="primary"
                    size="small"
                    onClick={() => setActiveImageSlot("banner")}
                    style={{
                      background: '#1890ff',
                      borderColor: '#1890ff',
                      borderRadius: '6px',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px'
                    }}
                  >
                    <i className="fa fa-edit" /> Edit Banner
                  </Button>
                )}
              </div>

              {/* Clean Banner Image Preview (Shows final visual exactly as designed in Studio) */}
              <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc', marginBottom: '20px' }}>
                {values.bannerImageUrl ? (
                  <img
                    src={values.bannerImageUrl}
                    alt="Banner Visual"
                    style={{ width: '100%', maxHeight: '420px', objectFit: 'contain', display: 'block' }}
                  />
                ) : (
                  <div className="banner-mockup-bg default-gradient" style={{ height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'static' }}>
                    <span style={{ color: '#ffffff', fontWeight: 600, fontSize: '15px', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                      No Banner Image Created Yet
                    </span>
                  </div>
                )}
              </div>

              {/* Offer Details Mockup (Shows the structured metadata of the offer) */}
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
                {(values.stickerLabel || values.bannerText) && (
                  <div style={{ marginBottom: '12px', display: 'flex', gap: '8px' }}>
                    {values.stickerLabel && (
                      <span style={{ background: '#3b82f6', color: '#ffffff', fontSize: '10px', fontWeight: 800, padding: '3px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {values.stickerLabel}
                      </span>
                    )}
                    {values.bannerText && (
                      <span style={{ background: '#ef4444', color: '#ffffff', fontSize: '10px', fontWeight: 800, padding: '3px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {values.bannerText}
                      </span>
                    )}
                  </div>
                )}
                <h4 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px 0', fontFamily: 'Outfit, sans-serif' }}>
                  {values.title || "Offer Title Mockup"}
                </h4>
                <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 16px 0', lineHeight: 1.5, fontFamily: 'Inter, sans-serif' }}>
                  {values.description || "Add a description to make this offer stand out."}
                </p>
                <div>
                  {values.ctaLink ? (
                    <a href={values.ctaLink} target="_blank" rel="noopener noreferrer" className="banner-mockup-btn" style={{ position: 'static', margin: 0, border: '1px solid #cbd5e1', display: 'inline-flex', alignItems: 'center' }}>
                      {values.ctaText || "Learn More"} <i className="fa fa-external-link ml-1" style={{ fontSize: '10px' }} />
                    </a>
                  ) : (
                    <button type="button" className="banner-mockup-btn" style={{ position: 'static', margin: 0, border: '1px solid #cbd5e1' }}>
                      {values.ctaText || "Learn More"}
                    </button>
                  )}
                </div>
              </div>

              {/* Coupon Code section */}
              {values.couponCode && (
                <div className="coupon-ticket-mockup">
                  <div className="coupon-ticket-border-left" />
                  <div className="coupon-ticket-body">
                    <span className="coupon-ticket-label">COUPON CODE</span>
                    <span className="coupon-ticket-code">{values.couponCode}</span>
                    <span className="coupon-ticket-subtext">Apply code at checkout to claim discount.</span>
                  </div>
                  <div className="coupon-ticket-border-right" />
                </div>
              )}

              {/* Subscription levels preview */}
              {previewItems.length > 0 && (
                <div className="offer-preview-prices-container" style={{ marginTop: '16px' }}>
                  <div className="prices-title">🎓 Pricing Preview for Learners</div>
                  <div className="prices-grid">
                    {previewItems.map((item, idx) => (
                      <div key={idx} className="price-item-card">
                        <div className="price-plan-name">{item.subName} - {item.optName}</div>
                        {item.priceType === 'FREE' ? (
                          <div className="price-new text-success">FREE</div>
                        ) : item.priceType === 'CONTACT_SALES' ? (
                          <div className="price-new text-warning">Contact Sales</div>
                        ) : (
                          <div>
                            {item.originalPrice !== null && (
                              <span className="price-old">{item.currency === 'INR' ? '₹' : item.currency}{item.originalPrice?.toLocaleString()}</span>
                            )}
                            <span className="price-new">
                              {item.currency === 'INR' ? '₹' : item.currency}{item.finalPrice?.toLocaleString()}
                              <span className="price-cycle">/{item.billingCycle.toLowerCase()}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 3: Submit button */}
        <div className="row">
          <div className="col-12">
            <div className="submit-section" style={{ marginTop: '0px', marginBottom: '30px' }}>
              {Object.keys(errors).length > 0 && (
                <div style={{ color: 'red', marginBottom: '10px' }}>
                  <strong>Validation Errors:</strong> {JSON.stringify(errors)}
                </div>
              )}
              <button type="submit" className="btn btn-primary submit-btn" style={{ marginTop: '0px' }}>
                {isUpdate && offerInfo?._id ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>

        {/* Studio Modal Rendering */}
        <ImageStudio
          visible={!!activeImageSlot}
          onClose={() => setActiveImageSlot(null)}
          onImageSaved={(url) => {
            if (activeImageSlot === "banner") setFieldValue("bannerImageUrl", url);
            else if (activeImageSlot === "patch") setFieldValue("patchImageUrl", url);
            else if (activeImageSlot === "square") setFieldValue("squareCardImageUrl", url);
            setActiveImageSlot(null);
          }}
          defaultSize={
            activeImageSlot === "banner" ? { width: 1200, height: 400 } :
            activeImageSlot === "patch" ? { width: 304, height: 152 } :
            activeImageSlot === "square" ? { width: 512, height: 512 } : { width: 1200, height: 400 }
          }
          initialImageUrl={
            activeImageSlot === "banner" ? values.bannerImageUrl :
            activeImageSlot === "patch" ? values.patchImageUrl :
            activeImageSlot === "square" ? values.squareCardImageUrl : ""
          }
          contextData={{
            discountValue: values.discountValue,
            discountType: values.discountType,
            merchantName: values.level === "MERCHANT" ? values.merchant : "",
          }}
        />
      </Form>
    </FormikProvider>
  );
}

export default FormView;

